/**
 * ============================================================================
 * LIVE PREVIEW MODULE
 * ============================================================================
 *
 * Real-time preview system for theme customization without page reload.
 * Integrates with Shopify Theme Editor for instant visual feedback.
 *
 * Features:
 * - Real-time color updates
 * - CSS custom property synchronization
 * - Theme editor integration
 * - Performance optimized updates
 * - Fallback handling
 *
 * @author DeSight Studio GmbH
 * @version 1.0.0
 * ============================================================================
 */

class LivePreview {
  constructor() {
    this.settings = window.CustomThemeSettings || {};
    this.isThemeEditor = window.Shopify && window.Shopify.designMode;
    this.updateQueue = new Map();
    this.updateTimer = null;
    this.updateDelay = 16; // ~60fps

    this.init();
  }

  /**
   * Initialize live preview system
   */
  init() {
    if (!this.settings.features?.livePreview) {
      return;
    }

    this.setupThemeEditorIntegration();
    this.setupMutationObserver();
    this.setupPerformanceOptimization();
    this.bindEvents();
  }

  /**
   * Setup Shopify Theme Editor integration
   */
  setupThemeEditorIntegration() {
    if (!this.isThemeEditor) return;

    // Listen for section changes
    document.addEventListener('shopify:section:load', (e) => {
      this.handleSectionChange(e.detail);
    });

    document.addEventListener('shopify:section:unload', (e) => {
      this.handleSectionChange(e.detail);
    });

    document.addEventListener('shopify:section:select', (e) => {
      this.handleSectionChange(e.detail);
    });

    document.addEventListener('shopify:section:deselect', (e) => {
      this.handleSectionChange(e.detail);
    });

    // Listen for block changes
    document.addEventListener('shopify:block:select', (e) => {
      this.handleBlockChange(e.detail);
    });

    document.addEventListener('shopify:block:deselect', (e) => {
      this.handleBlockChange(e.detail);
    });
  }

  /**
   * Setup mutation observer for DOM changes
   */
  setupMutationObserver() {
    // Observe changes to style elements and custom properties
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.tagName === 'STYLE' || node.querySelector('style')) {
                this.handleStyleChange();
              }
            }
          });
        }

        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          this.handleInlineStyleChange(mutation.target);
        }
      });
    });

    this.observer.observe(document.head, {
      childList: true,
      subtree: true
    });

    this.observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style']
    });
  }

  /**
   * Setup performance optimization
   */
  setupPerformanceOptimization() {
    // Use requestAnimationFrame for smooth updates
    this.scheduleUpdate = () => {
      if (this.updateTimer) return;

      this.updateTimer = requestAnimationFrame(() => {
        this.processUpdateQueue();
        this.updateTimer = null;
      });
    };
  }

  /**
   * Handle section changes from theme editor
   */
  handleSectionChange(detail) {
    // Re-parse custom schema variables
    this.updateCustomSchemaVariables();

    // Update color system
    this.updateColorSystem();

    // Emit section change event
    document.dispatchEvent(new CustomEvent('livePreview:sectionChanged', {
      detail
    }));
  }

  /**
   * Handle block changes from theme editor
   */
  handleBlockChange(detail) {
    // Update specific block styling
    this.updateBlockStyling(detail);

    // Emit block change event
    document.dispatchEvent(new CustomEvent('livePreview:blockChanged', {
      detail
    }));
  }

  /**
   * Handle style changes
   */
  handleStyleChange() {
    this.queueUpdate('styles', () => {
      this.updateCustomSchemaVariables();
      this.updateColorSystem();
    });
  }

  /**
   * Handle inline style changes
   */
  handleInlineStyleChange(element) {
    if (element === document.documentElement) {
      this.queueUpdate('rootStyles', () => {
        this.updateColorSystem();
      });
    }
  }

  /**
   * Update custom schema variables
   */
  updateCustomSchemaVariables() {
    // Find and re-evaluate custom schema variables
    const schemaScript = document.querySelector('script[data-custom-theme-settings]') ||
      Array.from(document.scripts).find(script =>
        script.textContent.includes('window.CustomThemeSettings'));

    if (schemaScript) {
      try {
        // Re-evaluate the script content
        const scriptContent = schemaScript.textContent;
        const settingsMatch = scriptContent.match(/window\.CustomThemeSettings\s*=\s*({[\s\S]*?});/);

        if (settingsMatch) {
          const newSettings = eval(`(${settingsMatch[1]})`);

          // Update global settings
          window.CustomThemeSettings = newSettings;
          this.settings = newSettings;

          // Emit settings update event
          document.dispatchEvent(new CustomEvent('livePreview:settingsUpdated', {
            detail: newSettings
          }));
        }
      } catch (error) {
        console.warn('Failed to update custom theme settings:', error);
      }
    }
  }

  /**
   * Update color system
   */
  updateColorSystem() {
    if (!this.settings.colors) return;

    // Update CSS custom properties
    Object.entries(this.settings.colors).forEach(([key, value]) => {
      const cssVar = `--color-custom-${this.camelToKebab(key)}`;
      const currentValue = getComputedStyle(document.documentElement)
        .getPropertyValue(cssVar).trim();

      if (currentValue !== value) {
        document.documentElement.style.setProperty(cssVar, value);
      }
    });

    // Update other custom properties
    if (this.settings.borderRadius) {
      Object.entries(this.settings.borderRadius).forEach(([key, value]) => {
        const cssVar = `--border-radius-custom-${key}`;
        document.documentElement.style.setProperty(cssVar, `${value}px`);
      });
    }

    if (this.settings.spacing) {
      Object.entries(this.settings.spacing).forEach(([key, value]) => {
        if (typeof value === 'number') {
          const cssVar = `--spacing-custom-${key}`;
          document.documentElement.style.setProperty(cssVar, `${value}px`);
        }
      });
    }

    // Emit color system update event
    document.dispatchEvent(new CustomEvent('livePreview:colorsUpdated', {
      detail: this.settings.colors
    }));
  }

  /**
   * Update block styling
   */
  updateBlockStyling(detail) {
    const blockElement = document.querySelector(`[data-block-id="${detail.blockId}"]`);
    if (!blockElement) return;

    // Apply any block-specific styling updates
    this.queueUpdate(`block-${detail.blockId}`, () => {
      // Block-specific update logic here
      blockElement.dispatchEvent(new CustomEvent('block:updated', {
        detail,
        bubbles: true
      }));
    });
  }

  /**
   * Queue an update for performance optimization
   */
  queueUpdate(key, updateFunction) {
    this.updateQueue.set(key, updateFunction);
    this.scheduleUpdate();
  }

  /**
   * Process the update queue
   */
  processUpdateQueue() {
    if (this.updateQueue.size === 0) return;

    // Execute all queued updates
    this.updateQueue.forEach((updateFunction, key) => {
      try {
        updateFunction();
      } catch (error) {
        console.warn(`Failed to execute update for ${key}:`, error);
      }
    });

    // Clear the queue
    this.updateQueue.clear();

    // Emit batch update complete event
    document.dispatchEvent(new CustomEvent('livePreview:batchUpdateComplete'));
  }

  /**
   * Manually trigger a full update
   */
  triggerFullUpdate() {
    this.updateCustomSchemaVariables();
    this.updateColorSystem();

    document.dispatchEvent(new CustomEvent('livePreview:fullUpdateComplete'));
  }

  /**
   * Enable/disable live preview
   */
  setEnabled(enabled) {
    this.settings.features = this.settings.features || {};
    this.settings.features.livePreview = enabled;

    if (enabled) {
      this.setupThemeEditorIntegration();
      this.setupMutationObserver();
    } else {
      if (this.observer) {
        this.observer.disconnect();
      }
    }

    document.dispatchEvent(new CustomEvent('livePreview:enabledChanged', {
      detail: { enabled }
    }));
  }

  /**
   * Get current preview state
   */
  getState() {
    return {
      enabled: this.settings.features?.livePreview || false,
      isThemeEditor: this.isThemeEditor,
      queueSize: this.updateQueue.size,
      settings: this.settings
    };
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Listen for settings changes
    document.addEventListener('custom-theme:settings-loaded', (e) => {
      this.settings = e.detail;
      this.triggerFullUpdate();
    });

    // Listen for manual update requests
    document.addEventListener('livePreview:requestUpdate', () => {
      this.triggerFullUpdate();
    });

    // Handle visibility changes for performance
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Pause updates when tab is hidden
        if (this.updateTimer) {
          cancelAnimationFrame(this.updateTimer);
          this.updateTimer = null;
        }
      } else {
        // Resume updates when tab becomes visible
        if (this.updateQueue.size > 0) {
          this.scheduleUpdate();
        }
      }
    });
  }

  /**
   * Cleanup resources
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.updateTimer) {
      cancelAnimationFrame(this.updateTimer);
    }

    this.updateQueue.clear();
  }

  // Utility methods

  /**
   * Convert camelCase to kebab-case
   */
  camelToKebab(str) {
    return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
  }
}

// Make LivePreview available globally
window.LivePreview = LivePreview;
